/**
 * Profit & ads tab (admins only; the API itself is admin-only): contribution and net profit for the week,
 * loss-making SKUs, TACOS by brand / category / platform and discount leakage against list price.
 * Props: data (GET /api/review/profit).
 */
import React, { useState } from 'react';
import { MetricToggle } from '../sales/views/common';
import { shortMoney } from '../sales/format';
import { DataTable, Delta, Metric, Money, Notes, Num, Pct, Section, SkuCell } from './ReviewPieces';

const pctChange = (current, previous) => (previous ? ((current - previous) / Math.abs(previous)) * 100 : null);
const TACOS_DIMS = [['brand', 'Brands'], ['category', 'Categories'], ['platform', 'Platforms']];

export const ProfitTab = ({ data }) => {
  const [dim, setDim] = useState('brand');
  if (!data) return null;
  const s = data.summary || {};
  const p = data.previous || {};
  const entry = key => ({ value: s[key], previous: p[key], wow_pct: pctChange(s[key], p[key]) });
  const tacosRows = data.tacos?.[dim] || [];
  return (
    <div className="space-y-4" data-testid="review-profit-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Net revenue (ex GST)" entry={entry('net_revenue')} />
        <Metric label="Contribution" entry={entry('contribution')} footnote={s.contribution_margin_pct != null ? `${s.contribution_margin_pct}% of net revenue · ${shortMoney(data.contribution_per_unit)} per unit` : undefined} testId="review-profit-contribution" />
        <Metric label="Ad spend" entry={entry('ad_spend')} invert />
        <Metric label="Net profit (after ads)" entry={entry('net_profit')} footnote={s.net_margin_pct != null ? `${s.net_margin_pct}% of net revenue` : undefined} />
        <Metric label="Cost of goods" entry={entry('cogs')} invert />
        <Metric label="Marketplace fees" entry={entry('fees')} invert />
        <Metric label="Units" entry={entry('units')} kind="number" />
        <Metric label="Uncosted units" entry={{ value: s.uncosted_units }} kind="number" footnote={data.uncosted_skus?.length ? `${data.uncosted_skus.length} SKU(s) have no cost price` : 'Every sold SKU has a cost price'} />
      </div>

      <Section title="Loss makers" subtitle="SKUs whose net profit is negative this week, after cost of goods, fees and ads." testId="review-loss-makers">
        <DataTable rows={data.loss_makers} empty="No loss-making SKU this week." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'units', label: 'Units', align: 'right', render: row => <Num v={row.units} /> },
          { key: 'revenue', label: 'Revenue', align: 'right', render: row => <Money v={row.revenue} /> },
          { key: 'cogs', label: 'COGS', align: 'right', render: row => <Money v={row.cogs} /> },
          { key: 'fees', label: 'Fees', align: 'right', render: row => <Money v={row.fees} /> },
          { key: 'ad_spend', label: 'Ads', align: 'right', render: row => <Money v={row.ad_spend} /> },
          { key: 'net_profit', label: 'Net profit', align: 'right', render: row => <span className="font-semibold text-red-700"><Money v={row.net_profit} /></span> },
        ]} />
      </Section>

      <Section title="Ad efficiency (TACOS)" subtitle="Ad spend as a share of revenue; contribution and net profit after ads." right={<MetricToggle value={dim} onChange={setDim} options={TACOS_DIMS} label="TACOS by" />}>
        <DataTable rows={tacosRows} rowKey={dim} empty="No ad spend or sales in this scope." columns={[
          { key: dim, label: dim[0].toUpperCase() + dim.slice(1) },
          { key: 'revenue', label: 'Revenue', align: 'right', render: row => <Money v={row.revenue} /> },
          { key: 'ad_spend', label: 'Ad spend', align: 'right', render: row => <Money v={row.ad_spend} /> },
          { key: 'tacos_pct', label: 'TACOS', align: 'right', render: row => <Pct v={row.tacos_pct} /> },
          { key: 'contribution', label: 'Contribution', align: 'right', render: row => <Money v={row.contribution} /> },
          { key: 'net_profit', label: 'Net profit', align: 'right', render: row => <span className={row.net_profit < 0 ? 'text-red-700' : ''}><Money v={row.net_profit} /></span> },
        ]} />
      </Section>

      <Section title="Discount leakage" subtitle="Units × (list price − realised price). SKUs listed when they sold below 85% of list."
        right={<span className="text-sm font-semibold tabular-nums"><Money v={data.discount_leakage?.total} /></span>}>
        {data.discount_leakage?.by_brand?.length > 0 && (
          <ul className="mb-3 flex flex-wrap gap-2 text-xs text-gray-700">
            {data.discount_leakage.by_brand.slice(0, 8).map(row => <li key={row.brand} className="rounded-full bg-gray-100 px-2.5 py-1">{row.brand}: {shortMoney(row.leakage)}</li>)}
          </ul>
        )}
        <DataTable rows={data.discount_leakage?.top_skus} empty="No SKU sold far below its list price." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'list_price', label: 'List', align: 'right', render: row => <Money v={row.list_price} /> },
          { key: 'realised_price', label: 'Realised', align: 'right', render: row => <Money v={row.realised_price} /> },
          { key: 'discount_pct', label: 'Discount', align: 'right', render: row => <Delta pct={-row.discount_pct} /> },
          { key: 'units', label: 'Units', align: 'right', render: row => <Num v={row.units} /> },
          { key: 'leakage', label: 'Leakage', align: 'right', render: row => <span className="font-semibold"><Money v={row.leakage} /></span> },
        ]} />
      </Section>

      <Section title="Contribution by SKU" subtitle={`Top ${Math.min(50, data.by_sku?.length || 0)} of ${data.by_sku?.length || 0} SKUs sold this week, by revenue.`} testId="review-profit-skus">
        <DataTable rows={data.by_sku?.slice(0, 50)} empty="No sales this week." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'units', label: 'Units', align: 'right', render: row => <Num v={row.units} /> },
          { key: 'revenue', label: 'Revenue', align: 'right', render: row => <Money v={row.revenue} /> },
          { key: 'contribution', label: 'Contribution', align: 'right', render: row => <span className={row.contribution < 0 ? 'text-red-700' : ''}><Money v={row.contribution} /></span> },
          { key: 'contribution_per_unit', label: 'Per unit', align: 'right', render: row => <Money v={row.contribution_per_unit} /> },
          { key: 'tacos_pct', label: 'TACOS', align: 'right', render: row => <Pct v={row.tacos_pct} /> },
          { key: 'net_profit', label: 'Net profit', align: 'right', render: row => <span className={row.net_profit < 0 ? 'text-red-700' : ''}><Money v={row.net_profit} /></span> },
        ]} />
      </Section>
      <Notes notes={data.notes} />
    </div>
  );
};

export default ProfitTab;
